import { useContext, useRef, useState } from 'react';
import { uiFormat, uiText } from '../../i18n';
import { record, rows, text } from '../studio/studio-content';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { SettingsDrillContext } from '../studio/settings-drill-context';
import { SettingsDrill } from '../studio/SettingsDrill';
import { StudioIcon } from '../studio/StudioIcon';
import { ChoiceTiles } from '../../ui/ChoiceTiles';
import { SwitchRow } from '../../ui/SwitchRow';
import { shake } from '../../ui/motion';
import { platform } from '../../platform';
import { StudioSettingsSection } from './StudioFormParts';
import { useShootDicts } from './use-studio-shoot';
import {
  shootContext,
  shootDefault,
  shootItem,
  shootItemName,
  shootKinds,
  shootLabels as labels,
  shootLens,
  shootCamera,
  shootRowOff,
  shootRowValue,
  shootVisible,
  type ShootItem,
  type ShootOverrides,
} from './studio-shoot-model';
import '../photo/photo-shooting.css';

/** Порядки меню ⇅ по строкам (§1.9): помнятся всё время работы приложения, сервер их не хранит. */
const SORTS: Record<string, string> = {};
const sortChoices = (kind: string) => [
  { value: 'popular', label: uiText('Популярные') },
  ...(kind === 'camera'
    ? [
        { value: 'new', label: uiText('Новые') },
        { value: 'old', label: uiText('Старые') },
      ]
    : kind === 'lens'
      ? [
          { value: 'wide', label: uiText('Широкие') },
          { value: 'long', label: uiText('Длинные') },
        ]
      : []),
  { value: 'alpha', label: uiText('От А до Я') },
];
const fold = (value: unknown) => text(value).toLocaleLowerCase('ru').replace(/ё/g, 'е');

/** Блок «Съёмка» в листе «Ещё настройки». overrides — контекст экрана (образ, сцена, референс, подпись, скрытые камеры товара);
    без них контекст читается из полей style и reference формы (как у «Фото» и «Видео»). */
export function StudioShooting({ state, overrides }: { state: CircleFormState; overrides?: ShootOverrides }) {
  const visible = shootKinds(state.form, state.inputs);
  return visible.length ? <StudioShootingFields state={state} kinds={visible} overrides={overrides} /> : null;
}
function StudioShootingFields({ state, kinds, overrides }: { state: CircleFormState; kinds: string[]; overrides?: ShootOverrides }) {
  const source = useShootDicts();
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('');
  const [sort, setSort] = useState('popular');
  const [hint, setHint] = useState<{ kind: string; text: string } | null>(null);
  const drill = useContext(SettingsDrillContext);
  const remembered = useRef<Record<string, string>>({});
  const catalog = record(record(source.data).shoot ?? source.data);
  const context = shootContext(state.form, state.inputs, source.data, overrides);
  if (!context) return null;
  const { items, selected } = context;
  const camera = shootCamera(context);
  const lens = shootLens(context);
  const say = (kind: string, reason: string, element: HTMLElement | null) => {
    shake(element);
    platform.haptic('light');
    setHint({ kind, text: reason });
  };
  return (
    <StudioSettingsSection id="shoot" title="Съёмка" hint="цена та же" kind="block">
      {source.loading && <p role="status">{uiText('Загружаем…')}</p>}
      {source.error && (
        <p role="alert">
          {uiText('Настройки съёмки недоступны.')}{' '}
          <button type="button" onClick={source.reload}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {source.data && (
        <>
          {kinds.map(kind => {
            const search = fold(query);
            const choices = items.filter(
              item =>
                shootVisible(context, item, kind) &&
                (!search || [item.name, item.sub, rows(catalog.groups).find(g => g.id === item.group)?.name].some(v => fold(v).includes(search))) &&
                (search || !group || item.group === group || !item.group),
            );
            choices.sort((a, b) =>
              sort === 'alpha'
                ? text(a.name).localeCompare(text(b.name), 'ru')
                : sort === 'new'
                  ? Number(record(b.years).from ?? -1) - Number(record(a.years).from ?? -1)
                  : sort === 'old'
                    ? Number(record(a.years).from ?? 9999) - Number(record(b.years).from ?? 9999)
                    : sort === 'wide'
                      ? Number(a.focal_mm ?? 9999) - Number(b.focal_mm ?? 9999)
                      : sort === 'long'
                        ? Number(b.focal_mm ?? -1) - Number(a.focal_mm ?? -1)
                        : Number(a.order ?? 0) - Number(b.order ?? 0),
            );
            const groups = rows(catalog.groups).filter(
              item => item.kind === kind && items.some(option => option.group === item.id && shootVisible(context, option, kind)),
            );
            const off = state.locked ? '' : shootRowOff(context, kind);
            const fallback = shootDefault(context, kind);
            const automatic = shootItem(context, kind, fallback) ?? shootItem(context, kind, 'auto');
            const current = selected[kind] ?? fallback;
            const value = shootRowValue(context, kind, state.form?.screen);
            const styleNote =
              ['light', 'color'].includes(kind) && context.style && !context.reference
                ? 'Стиль уже задаёт свет и цвет. Выберите другой, если хотите поменять'
                : '';
            const fLimit =
              kind === 'aperture' && typeof lens?.f_max === 'number' ? uiFormat('{0} открывается только до f/{1}', text(lens.name), lens.f_max) : '';
            const choose = (next: string) => {
              state.setInputs(previousInputs => {
                let previous: Record<string, string> = {};
                try {
                  previous = JSON.parse(previousInputs.shoot ?? '{}');
                } catch {
                  /* Заменяем повреждённый снимок. */
                }
                const result = { ...previous, [kind]: next };
                if (kind === 'camera') {
                  const chosenCamera = items.find(item => item.kind === 'camera' && item.id === next);
                  if (chosenCamera?.fixed_lens) {
                    if (!camera?.fixed_lens) remembered.current.lens = result.lens ?? 'auto';
                    result.lens = 'auto';
                  } else if (camera?.fixed_lens) result.lens = remembered.current.lens ?? 'auto';
                  if (chosenCamera?.fixed_aperture) {
                    if (!camera?.fixed_aperture) remembered.current.aperture = result.aperture ?? 'auto';
                    result.aperture = 'auto';
                  } else if (camera?.fixed_aperture) result.aperture = remembered.current.aperture ?? 'auto';
                }
                if (kind === 'camera' || kind === 'lens') {
                  const nextCamera = items.find(item => item.kind === 'camera' && item.id === result.camera);
                  const nextLens = nextCamera?.fixed_lens ? nextCamera : items.find(item => item.kind === 'lens' && item.id === result.lens);
                  const aperture = items.find(item => item.kind === 'aperture' && item.id === result.aperture);
                  if (
                    nextLens?.fixed_aperture ||
                    (typeof aperture?.f_stop === 'number' && typeof nextLens?.f_max === 'number' && aperture.f_stop < nextLens.f_max) ||
                    (result.aperture === 'open' && typeof nextLens?.f_max !== 'number')
                  )
                    result.aperture = 'auto';
                }
                return { ...previousInputs, shoot: JSON.stringify(result) };
              });
              drill?.back();
            };
            const tileOff = (item: ShootItem) =>
              item.requires === 'f_max' && typeof lens?.f_max !== 'number'
                ? 'Объектив не задаёт светосилу'
                : kind === 'aperture' && typeof item.f_stop === 'number' && typeof lens?.f_max === 'number' && item.f_stop < lens.f_max
                  ? uiFormat('Этот объектив открывается только до f/{0}', lens.f_max)
                  : '';
            return (
              <div key={kind}>
                <button
                  type="button"
                  className={`sc-r sc-dr${off ? ' off' : ''}${selected[kind] && selected[kind] !== fallback ? ' chg' : ''}`}
                  disabled={state.locked}
                  aria-disabled={off ? 'true' : undefined}
                  aria-label={`${uiText(labels[kind])}: ${uiText(value)}`}
                  onClick={event => {
                    if (off) return say(kind, off, event.currentTarget);
                    setHint(null);
                    setQuery('');
                    setGroup('');
                    setSort(SORTS[kind] ?? 'popular');
                    drill?.open(`shoot:${kind}`);
                  }}
                >
                  <span className="sc-th o">
                    <StudioIcon kind={kind === 'camera' ? 'camera' : kind === 'move' || kind === 'tempo' ? 'video' : 'sliders'} />
                  </span>
                  <span className="m">
                    <b>{uiText(labels[kind])}</b>
                    <small>{uiText(value)}</small>
                  </span>
                  <span className="ch">
                    <StudioIcon kind="chev" />
                  </span>
                </button>
                {hint?.kind === kind && (
                  <p className="studio-settings-tip" role="status">
                    {uiText(hint.text)}
                  </p>
                )}
                <SettingsDrill id={`shoot:${kind}`}>
                  <div className="photo-shoot-drill">
                    <div className="photo-shoot-drill-head">
                      <button type="button" className="iconb" aria-label={uiText('Назад к настройкам')} onClick={() => drill?.back()}>
                        <StudioIcon kind="back" />
                      </button>
                      <h2>{uiText(kind === 'move' ? 'Движение камеры' : labels[kind])}</h2>
                    </div>
                    <p className="sr-only">{text(rows(catalog.kinds).find(item => item.kind === kind)?.sheet_sub)}</p>
                    {kind !== 'aperture' && (
                      <>
                        <div className="search studio-style-search">
                          <StudioIcon kind="search" />
                          <input
                            type="search"
                            aria-label={uiText(text(rows(catalog.kinds).find(item => item.kind === kind)?.find) || 'Найти настройку съёмки')}
                            placeholder={uiText(text(rows(catalog.kinds).find(item => item.kind === kind)?.find).split(':')[0] || 'Найти')}
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                          />
                        </div>
                        <ChoiceTiles
                          label={uiText('Сначала')}
                          value={sort}
                          onChange={next => {
                            SORTS[kind] = next;
                            setSort(next);
                          }}
                          choices={sortChoices(kind)}
                        />
                      </>
                    )}
                    {!!groups.length && !query && (
                      <ChoiceTiles
                        label={uiText('Группа')}
                        value={group}
                        onChange={setGroup}
                        choices={[{ value: '', label: uiText('Все') }, ...groups.map(item => ({ value: text(item.id), label: text(item.name) }))]}
                      />
                    )}
                    {!!query && <p role="status">{uiFormat('Нашли {0}', choices.length)}</p>}
                    {styleNote && <p>{uiText(styleNote)}</p>}
                    <div className="photo-shoot-grid" role="radiogroup" aria-label={uiText(labels[kind])}>
                      {choices
                        .filter(item => item.id !== 'auto' && item.requires !== 'style' && item.requires !== 'reference')
                        .map(item => {
                          const reason = tileOff(item);
                          return (
                            <button
                              type="button"
                              role="radio"
                              aria-checked={current === item.id}
                              aria-disabled={reason ? 'true' : undefined}
                              className={`bcs-c${current === item.id ? ' on' : ''}${reason ? ' off' : ''}`}
                              key={text(item.id)}
                              onClick={event => (reason ? say(kind, reason, event.currentTarget) : choose(text(item.id)))}
                            >
                              <span className="pc">
                                <span className="bcs-no">
                                  <StudioIcon kind={kind === 'camera' ? 'camera' : 'sliders'} />
                                </span>
                                <i className="ck" aria-hidden="true">
                                  <StudioIcon kind="check" />
                                </i>
                              </span>
                              <b>{text(item.name)}</b>
                              <small>{text(item.sub)}</small>
                            </button>
                          );
                        })}
                    </div>
                    {fLimit && <p>{fLimit}</p>}
                    {hint?.kind === kind && (
                      <p className="studio-settings-tip" role="status">
                        {uiText(hint.text)}
                      </p>
                    )}
                    {!choices.length && <p role="status">{uiText('Ничего не нашлось. Попробуйте другое слово или сбросьте группу.')}</p>}
                    <p>{uiText('Выбор сразу вернёт к настройкам. Цена не меняется')}</p>
                    {automatic && (
                      <SwitchRow
                        title={shootItemName(context, automatic, state.form?.screen)}
                        description={text(automatic.sub)}
                        icon={<StudioIcon kind={kind === 'camera' ? 'camera' : 'sliders'} />}
                        checked={current === automatic.id}
                        disabled={state.locked}
                        onChange={() => {
                          if (current === automatic.id) setHint({ kind, text: 'Выключится, когда выберете вариант' });
                          else choose(text(automatic.id));
                        }}
                      />
                    )}
                  </div>
                </SettingsDrill>
              </div>
            );
          })}
        </>
      )}
    </StudioSettingsSection>
  );
}
